/**
 * IncentiveCalculationPage — `/franchise/incentives`.
 *
 * Real incentive engine backed by the operations slice:
 *   - Tier-bonus rules:    `/v1/franchise/ops/incentive-rules`
 *   - Computed grants:     `/v1/franchise/ops/incentive-grants/compute`,
 *                          `GET /v1/franchise/ops/incentive-grants/franchise/:id`
 *   - Approve a grant:     `/v1/franchise/ops/incentive-grants/:id/approve`
 *
 * The page also shows the underlying snapshot aggregate (revenue + base
 * commission) so operators can see how the bonus was derived.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  aggregateRevenue,
  approveIncentiveGrant,
  computeIncentiveGrant,
  createIncentiveRule,
  listFranchises,
  listIncentiveGrants,
  listIncentiveRules,
  type TierCode,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const TIERS: readonly TierCode[] = ['TIER_1', 'TIER_2', 'TIER_3'];

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoMinusDays(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function IncentiveCalculationPage(): JSX.Element {
  const qc = useQueryClient();
  const [franchiseId, setFranchiseId] = useState('');
  const [fromDate, setFromDate] = useState(() => isoMinusDays(30));
  const [toDate, setToDate] = useState(() => isoToday());
  const [showRule, setShowRule] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [ruleForm, setRuleForm] = useState({
    tierCode: 'TIER_1' as TierCode,
    name: '',
    revenueThreshold: '100000',
    bonusMultiplier: '1.10',
    flatBonus: '0',
  });

  const franchises = useQuery({
    queryKey: ['franchise', 'incentives', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const rules = useQuery({
    queryKey: ['franchise', 'incentives', 'rules'],
    queryFn: () => listIncentiveRules(),
    retry: false,
  });

  const agg = useQuery({
    queryKey: ['franchise', 'incentives', 'agg', franchiseId, fromDate, toDate],
    queryFn: () => aggregateRevenue(franchiseId, { fromDate, toDate }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const grants = useQuery({
    queryKey: ['franchise', 'incentives', 'grants', franchiseId],
    queryFn: () => listIncentiveGrants(franchiseId),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const compute = useMutation({
    mutationFn: () =>
      computeIncentiveGrant({
        franchiseId,
        periodStart: fromDate,
        periodEnd: toDate,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'incentives', 'grants', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const createRule = useMutation({
    mutationFn: () => {
      if (ruleForm.name.length < 2) throw new Error('Rule name is required.');
      return createIncentiveRule({
        tierCode: ruleForm.tierCode,
        name: ruleForm.name,
        revenueThreshold: ruleForm.revenueThreshold,
        bonusMultiplier: ruleForm.bonusMultiplier,
        flatBonus: ruleForm.flatBonus,
      });
    },
    onSuccess: () => {
      setShowRule(false);
      setRuleForm({
        tierCode: 'TIER_1',
        name: '',
        revenueThreshold: '100000',
        bonusMultiplier: '1.10',
        flatBonus: '0',
      });
      void qc.invalidateQueries({ queryKey: ['franchise', 'incentives', 'rules'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const approve = useMutation({
    mutationFn: (id: string) => approveIncentiveGrant(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'incentives', 'grants', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const denied =
    agg.isError && agg.error instanceof ApiError &&
    (agg.error.status === 401 || agg.error.status === 403);

  const selected = franchises.data?.items.find((f) => f.id === franchiseId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Incentive calculation"
        description="Tier-bonus rules + computed grants + approve workflow."
        actions={
          <button
            type="button"
            onClick={() => setShowRule((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {showRule ? 'Cancel' : 'New rule'}
          </button>
        }
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
          {franchises.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <select
              value={franchiseId}
              onChange={(e) => setFranchiseId(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm min-w-[260px]"
            >
              <option value="">Select…</option>
              {franchises.data?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legalName} ({f.franchiseNumber}) · {f.tierCode}
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          />
        </div>
        {franchiseId.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setActionErr(null);
              compute.mutate();
            }}
            disabled={compute.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {compute.isPending ? 'Computing…' : 'Compute grant'}
          </button>
        ) : null}
      </div>

      {showRule ? (
        <div className="bg-white border border-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900">Define a tier-bonus rule</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Tier</label>
              <select
                value={ruleForm.tierCode}
                onChange={(e) => setRuleForm({ ...ruleForm, tierCode: e.target.value as TierCode })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Name</label>
              <input
                type="text"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Threshold (NGN)</label>
              <input
                type="text"
                value={ruleForm.revenueThreshold}
                onChange={(e) =>
                  setRuleForm({ ...ruleForm, revenueThreshold: e.target.value })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Multiplier</label>
              <input
                type="text"
                value={ruleForm.bonusMultiplier}
                onChange={(e) =>
                  setRuleForm({ ...ruleForm, bonusMultiplier: e.target.value })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Flat bonus (NGN)</label>
              <input
                type="text"
                value={ruleForm.flatBonus}
                onChange={(e) => setRuleForm({ ...ruleForm, flatBonus: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setActionErr(null);
              createRule.mutate();
            }}
            disabled={createRule.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {createRule.isPending ? 'Saving…' : 'Save rule'}
          </button>
        </div>
      ) : null}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise to compute incentives.
        </p>
      ) : agg.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseRevenue/read</code> required.
        </DeferredNotice>
      ) : agg.isError ? (
        <DeferredNotice title="Aggregate unavailable">{(agg.error as Error).message}</DeferredNotice>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard title="Total revenue" value={formatNgn(agg.data?.totalRevenue ?? '0')} />
            <KPICard
              title="Base commission"
              value={formatNgn(agg.data?.totalCommission ?? '0')}
              hint={`Tier ${selected?.tierCode ?? '—'} · rate ${selected?.commissionRate ?? '—'}`}
            />
            <KPICard
              title="Snapshots"
              value={String(agg.data?.snapshotCount ?? 0)}
            />
            <KPICard
              title="Total incentive"
              value={formatNgn(grants.data?.totalGranted ?? '0')}
              hint={`${grants.data?.items.length ?? 0} grant(s)`}
            />
          </div>

          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Computed incentive grants</h3>
              <span className="text-xs text-muted">{grants.data?.items.length ?? 0} record(s)</span>
            </div>
            {(grants.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-muted px-5 py-6 text-center">
                No grants yet — pick a period and click <em>Compute grant</em>.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Period</th>
                    <th className="text-right px-4 py-2">Base</th>
                    <th className="text-right px-4 py-2">Bonus</th>
                    <th className="text-right px-4 py-2">Total</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {grants.data?.items.map((g) => (
                    <tr key={g.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-muted">
                        {g.periodStart} → {g.periodEnd}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {formatNgn(g.baseCommission)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {formatNgn(g.bonusAmount)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs font-semibold">
                        {formatNgn(g.totalIncentive)}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={g.status} />
                      </td>
                      <td className="px-4 py-2 text-right">
                        {g.status === 'PENDING' ? (
                          <button
                            type="button"
                            onClick={() => {
                              setActionErr(null);
                              approve.mutate(g.id);
                            }}
                            disabled={approve.isPending}
                            className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                          >
                            Approve →
                          </button>
                        ) : (
                          <span className="text-xs text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-semibold text-gray-900">Active incentive rules</h3>
            </div>
            {(rules.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-muted px-5 py-6 text-center">
                No rules defined yet — click <em>New rule</em> to add one.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-left px-4 py-2">Tier</th>
                    <th className="text-right px-4 py-2">Threshold</th>
                    <th className="text-right px-4 py-2">Multiplier</th>
                    <th className="text-right px-4 py-2">Flat bonus</th>
                    <th className="text-left px-4 py-2">Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rules.data?.items.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-700">{r.name}</td>
                      <td className="px-4 py-2 text-xs">{r.tierCode}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {formatNgn(r.revenueThreshold)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{r.bonusMultiplier}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {formatNgn(r.flatBonus)}
                      </td>
                      <td className="px-4 py-2 text-xs">{r.active ? 'YES' : 'NO'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
